import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import Highlighter from 'react-highlight-words';
import { Translate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import SystemBellIcon from 'resources/svg/alerts_bells/system.svg';
import CheckedCircleIcon from 'resources/svg/checked_with_special_circle.svg';
import { trackMixpanelEvent } from 'utils/general';
import { IconSizes, IconVariants } from '../../components/foundations/Icon';
import Tooltip from '../../components/molecules/Tooltip';
import { Icon } from '../../components/widgets';
import { getReadOnly } from '../../selectors/permissions';
import { RAN_TEST_REPORT, REPORT_TYPE_MAP, STATUS_MESSAGE_MAP, TRACK_EVENT_PREFIX } from '../consts';
import css from './AlertItem.css';

const AlertItem = ({
    alert = {},
    reportTypes = [],
    userData = {},
    onSetReportType = () => {},
    openDeleteDialog = () => {},
    openShelf = () => {},
    onSetSelectedAlert = () => {},
    isSystemAlert = false,
    systemAlerts = {},
    searchedText = '',
    readOnly: readOnlyProp = false,
}) => {
    const readOnlySelector = useSelector(getReadOnly);
    const readOnly = readOnlyProp || readOnlySelector;

    const BellIcon = alert.report_type ? REPORT_TYPE_MAP[alert.report_type].BellIcon : SystemBellIcon;
    const iconProps = { variant: IconVariants.LIGHT, size: IconSizes.MEDIUM };

    const isSubscribed = () => {
        if (isSystemAlert) {
            return (
                systemAlerts.data_connector_login_alert.emails.includes(userData.email) ||
                systemAlerts.data_delay_alert.emails.includes(userData.email) ||
                (userData.is_admin &&
                    (systemAlerts.data_connector_login_alert.send_to_admins ||
                        systemAlerts.data_delay_alert.send_to_admins))
            );
        }
        return alert.emails.includes(userData.email) || (userData.is_admin && alert.send_to_admins);
    };

    const getSubHeader = () => {
        if (isSystemAlert) return alert.subHeader;
        const createdString = new Date(alert.created).toLocaleString('default', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
        return `Created by ${alert.creator} on ${createdString}`;
    };

    const getDimensionString = (dimensionList, title) => {
        let dimensionsString = '';

        if (!dimensionList.length) return null;

        dimensionsString += dimensionList[0].display_name;

        if (dimensionList.length > 1) {
            dimensionsString += `, ${dimensionList[1].display_name}`;
        }

        if (dimensionList.length > 2) {
            dimensionsString += ` and ${dimensionList.length - 2} more`;
        }

        dimensionsString = dimensionsString ? title + dimensionsString : null;
        return dimensionsString;
    };

    const getFiltersList = dimensionName => {
        const sourceFiltersList = alert.filters
            .filter(filter => filter.dimension === dimensionName && filter.operator === 'in')
            .map(filter => filter.values);

        return sourceFiltersList.length ? sourceFiltersList[0] : [];
    };

    const getAlertDetails = () => {
        if (isSystemAlert) return '';

        const appString = getDimensionString(getFiltersList('app'), 'Apps: ');
        const sourceString = getDimensionString(getFiltersList('source'), 'Sources: ');
        const breakdownString = getDimensionString(alert.breakdown_dimensions, 'Broken down by: ');
        const alertDetails = [appString, sourceString, breakdownString].filter(detail => detail).join(' | ');

        return alertDetails;
    };

    const alertErrorMessage = alert.status !== 'OK' ? alert.status_message : '';
    const alertDetails = getAlertDetails();

    const onClickDelete = () => {
        onSetSelectedAlert(alert);
        openDeleteDialog();
    };
    const handleOnClick = () => {
        if (!isSystemAlert) {
            const reportType = reportTypes.filter(({ name }) => name === alert.report_type)[0];
            onSetReportType(reportType);
        }

        openShelf();
        onSetSelectedAlert(alert);
    };

    return (
        <div
            className={css.itemContainer}
            onClick={handleOnClick}
            style={{
                borderLeft: alert.report_type ? `3px solid ${REPORT_TYPE_MAP[alert.report_type].borderColor}` : '',
            }}
        >
            <div className={css.itemLeft}>
                <BellIcon className={css.logo} />
                <div className={css.itemHeaders}>
                    <div className={css.itemHeader}>
                        <Highlighter
                            highlightClassName={css.highlighted}
                            searchWords={[searchedText]}
                            textToHighlight={alert.name}
                            autoEscape
                            className={css.itemHeaderName}
                        />
                        {isSubscribed() && (
                            <div className={css.itemHeaderSubscribed}>
                                <div className={css.itemHeaderPipe} style={{ fontSize: '16px' }}>
                                    {' | '}
                                </div>
                                <div className={css.itemHeaderSubscribed} style={{ fontSize: '12px' }}>
                                    <Translate id="STATIC.PAGES.ALERTS_V2.ALERT_ITEM.SUBSCRIBED" />
                                </div>
                                <CheckedCircleIcon className={css.itemHeaderSubscribedIcon} />
                            </div>
                        )}
                    </div>
                    <Highlighter
                        highlightClassName={css.highlighted}
                        searchWords={[searchedText]}
                        textToHighlight={getSubHeader()}
                        autoEscape
                        className={css.itemSubHeader}
                    />
                </div>
            </div>
            <div className={css.itemRight}>
                {alertErrorMessage && (
                    <div
                        className={classNames(css.itemErrorDetails, { [css.itemDetails]: !alertDetails })}
                        style={{ color: STATUS_MESSAGE_MAP[alert.status] }}
                    >
                        {alertErrorMessage}
                    </div>
                )}
                {alertDetails && (
                    <div className={css.itemDetails}>{alertErrorMessage ? `  | ${alertDetails}` : alertDetails}</div>
                )}
                <div
                    className={classNames(css.alertButtons, {
                        [css.EditIconOnly]: isSystemAlert,
                        [css.noAlertDetails]: !alertDetails && !alertErrorMessage,
                    })}
                >
                    {!isSystemAlert && (
                        <>
                            {!readOnly && (
                                <Tooltip interactive titleTranslationKey="STATIC.PAGES.ALERTS_V2.DELETE">
                                    <Icon
                                        name="trash"
                                        onClick={e => {
                                            onClickDelete();
                                            e.stopPropagation();
                                        }}
                                        {...iconProps}
                                    />
                                </Tooltip>
                            )}
                            <Tooltip interactive titleTranslationKey="STATIC.PAGES.ALERTS_V2.TEST">
                                <a
                                    href={alert.report_url}
                                    target="_blank"
                                    onClick={e => {
                                        e.stopPropagation();
                                        trackMixpanelEvent(TRACK_EVENT_PREFIX, RAN_TEST_REPORT, {
                                            alert_id: alert.id,
                                            alert_name: alert.name,
                                            report_url: alert.report_url,
                                        });
                                    }}
                                    rel="noreferrer"
                                >
                                    <Icon name="flask" {...iconProps} />
                                </a>
                            </Tooltip>
                        </>
                    )}
                    {readOnly ? (
                        <Tooltip interactive titleTranslationKey="STATIC.PAGES.ALERTS_V2.VIEW">
                            <Icon name="eye" {...iconProps} />
                        </Tooltip>
                    ) : (
                        <Tooltip interactive titleTranslationKey="STATIC.PAGES.ALERTS_V2.EDIT">
                            <Icon name="pencil" {...iconProps} />
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};

AlertItem.propTypes = {
    alert: PropTypes.objectOf(PropTypes.any),
    reportTypes: PropTypes.arrayOf(PropTypes.object),
    userData: PropTypes.objectOf(PropTypes.any),
    onSetReportType: PropTypes.func,
    openDeleteDialog: PropTypes.func,
    openShelf: PropTypes.func,
    onSetSelectedAlert: PropTypes.func,
    isSystemAlert: PropTypes.bool,
    systemAlerts: PropTypes.objectOf(PropTypes.any),
    searchedText: PropTypes.string,
    readOnly: PropTypes.bool,
};

export default AlertItem;
